import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import { Dossier, DossierRequest, getDossier } from '../api/dossier';

export interface DossierMonitor {
    dossier: Dossier | null;
    request: DossierRequest | null;
    loading: boolean;
    error: string | null;
    /** False when it could not start yet: no API client, customer or environment. */
    open: (request: DossierRequest) => boolean;
    cancel: () => void;
    clear: () => void;
}

/**
 * Loads the dossier of one id for one environment.
 *
 * Opening another one clears the previous result straight away: a dossier of the wrong
 * invoice that stays on screen while the right one loads is read as the answer.
 */
export const useDossier = (
    customer: string | undefined,
    environment: string | undefined
): DossierMonitor => {
    const axios = useAxiosSwagger();
    const [dossier, setDossier] = useState<Dossier | null>(null);
    const [request, setRequest] = useState<DossierRequest | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const controller = useRef<AbortController | null>(null);

    const cancel = useCallback(() => {
        controller.current?.abort();
        controller.current = null;
        setLoading(false);
    }, []);

    const open = useCallback(
        (next: DossierRequest) => {
            const id = next.id.trim();

            if (!axios || !customer || !environment || !id) {
                return false;
            }

            controller.current?.abort();
            const current = new AbortController();
            controller.current = current;

            setDossier(null);
            setRequest({ ...next, id });
            setLoading(true);
            setError(null);

            getDossier(
                axios,
                customer,
                environment,
                { ...next, id },
                current.signal
            )
                .then((result) => {
                    if (controller.current === current) {
                        setDossier(result);
                    }
                })
                .catch((ex) => {
                    if (
                        controller.current === current &&
                        !current.signal.aborted
                    ) {
                        setError(describeError(ex));
                    }
                })
                .finally(() => {
                    if (controller.current === current) {
                        controller.current = null;
                        setLoading(false);
                    }
                });

            return true;
        },
        [axios, customer, environment]
    );

    const clear = useCallback(() => {
        cancel();
        setDossier(null);
        setRequest(null);
        setError(null);
    }, [cancel]);

    // Another customer or environment is another dossier altogether.
    // Cleared only when the environment really changes. As an effect cleanup it also ran on
    // React's development double-mount, right after a dossier opened from a link had started
    // loading: the panel stood open, empty, and the link counted as handled.
    const scope = `${customer ?? ''}|${environment ?? ''}`;
    const lastScope = useRef<string>(scope);

    useEffect(() => {
        if (lastScope.current !== scope) {
            lastScope.current = scope;
            clear();
        }
    }, [scope, clear]);

    return { dossier, request, loading, error, open, cancel, clear };
};
